<template>
  <div
    class="ai-conversation-panel"
    :data-ai-busy="isLoadingResponse || aiStore.assistant.isHandlingAgentAction || undefined"
  >
    <div class="conversation-container">
      <!-- Pinned banner when the panel has no connection to the current page -->
      <div v-if="!props.context" class="no-page-context-notice">
        <v-icon size="18" color="primary" class="mt-1 flex-shrink-0"
          >mdi-information-outline</v-icon
        >
        <span class="ml-2">{{ t('aiAssistant.conversation.noPageContext') }}</span>
      </div>
      <!-- Chat Messages Area -->
      <div ref="chatMessagesContainer" class="chat-messages" data-id-e2e="aiChatMessages">
        <div v-if="conversationMessages.length === 0" class="empty-conversation">
          <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-chat-outline</v-icon>
          <h4 class="text-h6 mb-2">{{ t('aiAssistant.conversation.title') }}</h4>
          <p class="text-body-2 mt-2">{{ t('aiAssistant.conversation.placeholder') }}</p>

          <AiDisclaimerBox />
        </div>

        <template v-for="(group, groupIdx) in conversationGroups" :key="groupIdx">
          <!-- User Question Bubble with following Assistant Answer(s) -->
          <div class="question-group mb-4">
            <v-card v-if="!group.user.isActionFeedback" class="question-bubble mb-2" elevation="1">
              <v-card-text class="py-2">
                <div class="question-text">
                  {{ group.user.content }}
                </div>
              </v-card-text>
            </v-card>

            <!-- Assistant Answer Cards — one per step in the chain -->
            <template v-for="(msg, msgIdx) in group.assistants" :key="msgIdx">
              <v-card
                class="answer-card"
                :class="{ 'mb-2': msgIdx < group.assistants.length - 1 }"
                elevation="2"
                :data-id-e2e="
                  groupIdx === conversationGroups.length - 1 &&
                  msgIdx === group.assistants.length - 1
                    ? 'aiLastResponse'
                    : undefined
                "
                @mouseenter="hoveredMessageId = msg.messageId"
                @mouseleave="hoveredMessageId = null"
              >
                <v-card-text
                  :class="
                    msg.isPlaceholder || msg.isCompletedStep
                      ? 'py-1'
                      : msg.isChart || msg.isTable
                        ? 'pa-0'
                        : undefined
                  "
                >
                  <div v-if="msg.isError" class="answer-error">
                    <v-icon size="16" color="error">mdi-alert-circle-outline</v-icon>
                    <span class="answer-error__message"
                      >Something went wrong. Please try again.</span
                    >
                  </div>
                  <div
                    v-else-if="msg.isPlaceholder"
                    class="answer-working"
                    data-id-e2e="aiActionWorking"
                  >
                    <span class="action-step-icon"
                      ><v-progress-circular indeterminate size="10" width="1.5"
                    /></span>
                    <span v-if="msg.workingLabel">{{ msg.workingLabel }}</span>
                  </div>
                  <div
                    v-else-if="msg.isCompletedStep"
                    class="answer-completed-step"
                    :data-id-e2e="`aiActionCompleted-${msg.stepOutcome || 'success'}`"
                  >
                    <span class="action-step-icon">
                      <v-icon
                        v-if="msg.stepOutcome === 'failed' || msg.stepOutcome === 'declined'"
                        size="14"
                        color="error"
                        >mdi-close-circle-outline</v-icon
                      >
                      <v-icon v-else size="14">mdi-check-circle-outline</v-icon>
                    </span>
                    <span v-if="msg.workingLabel">{{ msg.workingLabel }}</span>
                  </div>
                  <AiChartMessage v-else-if="msg.isChart" :chart-data="msg.chartData" />
                  <AiTableMessage v-else-if="msg.isTable" :table-data="msg.tableData" />
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div v-else class="answer-content" v-html="formatMarkdown(msg.content)" />
                </v-card-text>
                <div
                  v-if="msg.messageId && !msg.isError"
                  class="message-feedback-actions"
                  :class="{
                    visible: hoveredMessageId === msg.messageId || messageFeedback[msg.messageId],
                  }"
                >
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    data-id-e2e="aiMessageThumbUpBtn"
                    :class="{ 'thumb-rated': messageFeedback[msg.messageId]?.rating === 'up' }"
                    @click.stop="handleMessageFeedback(msg.messageId, 'up')"
                  >
                    <v-icon size="14">{{
                      messageFeedback[msg.messageId]?.rating === 'up'
                        ? 'mdi-thumb-up'
                        : 'mdi-thumb-up-outline'
                    }}</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    data-id-e2e="aiMessageThumbDownBtn"
                    :class="{
                      'thumb-rated thumb-rated--down':
                        messageFeedback[msg.messageId]?.rating === 'down',
                    }"
                    @click.stop="handleMessageFeedback(msg.messageId, 'down')"
                  >
                    <v-icon size="14">{{
                      messageFeedback[msg.messageId]?.rating === 'down'
                        ? 'mdi-thumb-down'
                        : 'mdi-thumb-down-outline'
                    }}</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </template>
          </div>
        </template>

        <!-- Loading indicator -->
        <div v-if="isLoadingResponse" class="question-group mb-4">
          <v-card class="answer-card" elevation="2">
            <v-card-text>
              <div class="answer-loading" data-id-e2e="aiAgentTyping">
                <v-progress-circular indeterminate color="primary" size="24" />
                <span class="ml-3">{{ isStopping ? 'Stopping...' : 'Thinking...' }}</span>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Action confirmation prompt — shown when the agent wants to execute a mutating tool -->
        <AiToolConfirmationPrompt />
      </div>

      <!-- Chat Input Area -->
      <div class="chat-input-container">
        <!-- Warning alerts above textarea -->
        <div class="warning-container">
          <div
            v-if="isContextLoading"
            class="limit-warning limit-warning--info"
            data-id-e2e="aiContextLoading"
          >
            <v-progress-circular indeterminate size="12" width="2" class="mr-2" />
            <span class="warning-text">{{ t('aiAssistant.conversation.contextLoading') }}</span>
          </div>

          <div v-if="limitStatus?.severity === 'info'" class="limit-warning limit-warning--info">
            <v-icon size="14" class="mr-2">mdi-information-outline</v-icon>
            <span class="warning-text">{{ limitStatus.message }}</span>
          </div>

          <div
            v-if="limitStatus?.severity === 'warning'"
            class="limit-warning limit-warning--warning"
          >
            <v-icon size="14" class="mr-2">mdi-alert-outline</v-icon>
            <span class="warning-text">{{ limitStatus.message }}</span>
          </div>

          <div v-if="limitStatus?.severity === 'error'" class="limit-warning limit-warning--error">
            <v-icon size="14" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span class="warning-text">{{ limitStatus.message }}</span>
          </div>
        </div>

        <div class="input-wrapper">
          <v-textarea
            ref="inputTextarea"
            v-model="conversationInput"
            data-id-e2e="aiAgentInput"
            placeholder="Type your question..."
            rows="5"
            auto-grow
            max-rows="8"
            variant="outlined"
            density="comfortable"
            hide-details
            @keydown.enter.exact="handleEnterKey"
          />
        </div>

        <div class="bottom-actions">
          <div class="bottom-actions__secondary">
            <v-btn
              variant="flat"
              :ripple="false"
              :disabled="isLoadingResponse"
              class="new-conversation-btn"
              data-id-e2e="aiNewConversationBtn"
              @click="handleStartNew"
            >
              <v-icon start size="14">mdi-message-plus</v-icon>
              New Conversation
            </v-btn>

            <v-btn
              v-if="hasInsightPrompts"
              variant="flat"
              :ripple="false"
              class="insights-btn"
              :class="{ 'insights-btn--active': showInsightsPanel }"
              data-id-e2e="aiInsightsBtn"
              @click="toggleInsightsPanel"
            >
              <v-icon start size="14">mdi-lightbulb-outline</v-icon>
              Insights
            </v-btn>

            <v-btn
              v-if="conversationMessages.length > 0"
              variant="flat"
              :ripple="false"
              class="feedback-btn"
              :class="{ 'feedback-btn--active': showFeedbackPanel }"
              data-id-e2e="aiConversationFeedbackBtn"
              @click="toggleFeedbackPanel"
            >
              <v-icon start size="14">{{
                conversationFeedback?.rating === 'up'
                  ? 'mdi-thumb-up'
                  : conversationFeedback?.rating === 'down'
                    ? 'mdi-thumb-down'
                    : 'mdi-thumb-up-outline'
              }}</v-icon>
              {{ t('aiAssistant.conversation.feedback.button') }}
            </v-btn>
          </div>

          <v-btn
            v-if="isLoadingResponse || aiStore.assistant.isHandlingAgentAction"
            variant="flat"
            :ripple="false"
            color="error"
            class="send-button"
            data-id-e2e="aiStopBtn"
            @click="handleStop"
          >
            <v-icon start size="14">mdi-stop</v-icon>
            Stop
          </v-btn>
          <v-btn
            v-else
            variant="flat"
            :ripple="false"
            class="send-button"
            :disabled="
              !conversationInput.trim() ||
              isLoadingResponse ||
              isContextLoading ||
              limitStatus?.severity === 'error'
            "
            data-id-e2e="aiSendBtn"
            @click="sendMessage"
          >
            <v-icon start size="14">mdi-send</v-icon>
            Send
          </v-btn>
        </div>

        <!-- Inline Insights Panel -->
        <transition name="slide-up">
          <div v-if="showInsightsPanel" class="insights-panel" data-id-e2e="aiInsightsPanel">
            <div class="insights-panel__header">
              <span class="insights-panel__title">Suggested insights</span>
            </div>
            <div class="insights-panel__chips">
              <v-chip
                v-for="item in props.context?.insightPrompts"
                :key="item.label"
                size="small"
                class="insight-chip"
                @click="sendInsightPrompt(item.prompt)"
              >
                {{ item.label }}
              </v-chip>
            </div>
          </div>
        </transition>

        <!-- Inline Feedback Panel -->
        <transition name="slide-up">
          <div
            v-if="showFeedbackPanel"
            class="feedback-panel"
            data-id-e2e="aiConversationFeedbackPanel"
          >
            <div class="feedback-panel__header">
              <span class="feedback-panel__title">{{
                t('aiAssistant.conversation.feedback.title')
              }}</span>
            </div>
            <div class="feedback-panel__body">
              <div class="feedback-thumbs">
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  data-id-e2e="aiConversationFeedbackThumbUp"
                  :class="{ 'thumb-rated': feedbackRating === 'up' }"
                  @click="feedbackRating = feedbackRating === 'up' ? null : 'up'"
                >
                  <v-icon size="20">{{
                    feedbackRating === 'up' ? 'mdi-thumb-up' : 'mdi-thumb-up-outline'
                  }}</v-icon>
                </v-btn>
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  data-id-e2e="aiConversationFeedbackThumbDown"
                  :class="{ 'thumb-rated thumb-rated--down': feedbackRating === 'down' }"
                  @click="feedbackRating = feedbackRating === 'down' ? null : 'down'"
                >
                  <v-icon size="20">{{
                    feedbackRating === 'down' ? 'mdi-thumb-down' : 'mdi-thumb-down-outline'
                  }}</v-icon>
                </v-btn>
              </div>
              <v-textarea
                v-model="feedbackComment"
                :placeholder="t('aiAssistant.conversation.feedback.placeholder')"
                rows="2"
                auto-grow
                max-rows="4"
                variant="outlined"
                density="compact"
                hide-details
                class="feedback-comment"
                data-id-e2e="aiConversationFeedbackComment"
              />
              <v-btn
                :ripple="false"
                variant="flat"
                :disabled="!feedbackRating || feedbackSubmitting"
                :loading="feedbackSubmitting"
                class="submit-btn"
                data-id-e2e="aiConversationFeedbackSubmitBtn"
                @click="submitConversationFeedback"
                >{{ t('aiAssistant.conversation.feedback.submit') }}</v-btn
              >
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- New Conversation Confirmation Dialog -->
    <!-- ai-dialog--full-viewport: opt out of the panel-offset restriction so this
         dialog retains a full-screen scrim even when the AI panel is open -->
    <v-dialog
      v-model="showConfirmDialog"
      max-width="450"
      persistent
      data-id-e2e="aiNewConversationDialog"
      class="ai-dialog--full-viewport"
    >
      <BaseDialogCard
        :title="t('aiAssistant.conversation.newConversation.title')"
        @close="showConfirmDialog = false"
      >
        <p>{{ t('aiAssistant.conversation.newConversation.message') }}</p>

        <template #footer>
          <v-card-actions class="justify-center">
            <v-btn
              variant="text"
              data-id-e2e="aiNewConversationCancelBtn"
              @click="showConfirmDialog = false"
            >
              {{ t('actions.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              data-id-e2e="aiNewConversationConfirmBtn"
              @click="confirmStartNew"
            >
              {{ t('aiAssistant.conversation.newConversation.confirm') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue';
import { useAiStore } from '@/stores/ai';
import { useInternationalization } from '@/composables/use-internationalization';
import { formatMarkdown } from '@/composables/use-ai-formatting';
import AiDisclaimerBox from '@/components/AiDisclaimerBox.vue';
import AiChartMessage from '@/components/AiChartMessage.vue';
import AiTableMessage from '@/components/AiTableMessage.vue';
import AiToolConfirmationPrompt from '@/components/AiToolConfirmationPrompt.vue';
import BaseDialogCard from '@/components/BaseDialogCard.vue';

// Composables
const { t } = useInternationalization();

// Props
const props = defineProps({
  context: {
    type: Object,
    default: null,
  },
});

// Stores
const aiStore = useAiStore();

// Context ID for scroll position tracking (separate from insights panel)
const contextId = 'assistant-conversation';

// Refs
const conversationInput = ref('');
const chatMessagesContainer = ref(null);
const inputTextarea = ref(null);
const showConfirmDialog = ref(false);
const showFeedbackPanel = ref(false);
const showInsightsPanel = ref(false);
const feedbackRating = ref(null);
const feedbackComment = ref('');
const userHasScrolledUp = ref(false);
const feedbackSubmitting = ref(false);
const hoveredMessageId = ref(null);
const isStopping = ref(false);

// Computed properties
const conversationMessages = computed(() => aiStore.assistant.messages);

// Groups messages into { user, assistants[] } pairs so that consecutive assistant
// steps (placeholder → completed-step → final answer) can all be rendered under
// the same user question bubble.
const conversationGroups = computed(() => {
  const groups = [];
  const msgs = conversationMessages.value;
  let i = 0;
  while (i < msgs.length) {
    if (msgs[i].role === 'user') {
      const group = { user: msgs[i], assistants: [] };
      i += 1;
      while (i < msgs.length && msgs[i].role === 'assistant') {
        group.assistants.push(msgs[i]);
        i += 1;
      }
      groups.push(group);
    } else {
      i += 1;
    }
  }
  return groups;
});

const isLoadingResponse = computed(() => aiStore.assistant.isLoading);

// Reset isStopping once both the LLM loading state and handler execution complete
watch(
  () => isLoadingResponse.value || aiStore.assistant.isHandlingAgentAction,
  active => {
    if (!active) isStopping.value = false;
  }
);

const isContextLoading = computed(
  () => !aiStore.sidePanel.contextReady && aiStore.sidePanel.contextFactory !== null
);
const conversationFeedback = computed(() => aiStore.assistant.conversationFeedback);
const messageFeedback = computed(() => aiStore.assistant.messageFeedback);
const hasInsightPrompts = computed(() => (props.context?.insightPrompts?.length ?? 0) > 0);
const limitStatus = computed(() => {
  const status = aiStore.sidePanel.limitStatus;
  if (!status || !status.severity) return null;

  // Map status to translation key
  const messageKey = {
    info: 'aiAssistant.conversation.limits.softWarning',
    warning: 'aiAssistant.conversation.limits.nearWarning',
    error: 'aiAssistant.conversation.limits.hardLimit',
  }[status.severity];

  return {
    severity: status.severity,
    message: t(messageKey),
  };
});

// Scroll helpers — defined first because sendMessage depends on them
function scrollToBottom() {
  if (chatMessagesContainer.value) {
    chatMessagesContainer.value.scrollTop = chatMessagesContainer.value.scrollHeight;
  }
}

async function sendMessage() {
  const message = conversationInput.value.trim();
  if (!message || isLoadingResponse.value) return;

  // A new user message always re-enables auto-scroll
  userHasScrolledUp.value = false;

  if (limitStatus.value?.severity === 'error') {
    console.warn('Cannot send message: conversation has reached hard limit');
    return;
  }

  conversationInput.value = '';
  await nextTick();
  scrollToBottom();

  try {
    if (!aiStore.assistant.currentConversationId) {
      const payload = {
        title: 'AI Assistant Conversation',
      };
      if (props.context?.workpackageId) {
        payload.workpackageId = props.context.workpackageId;
      }
      if (props.context?.scenarioId) {
        payload.scenarioId = props.context.scenarioId;
      }
      // Note: section and context are automatically extracted from aiStore.sidePanel.context
      await aiStore.createAssistantConversation(payload);
    }

    await aiStore.sendAssistantMessage(message);
  } catch (error) {
    console.error('Error sending message:', error);
  }
}

async function confirmStartNew() {
  showConfirmDialog.value = false;
  try {
    await aiStore.createNewConversation();
  } catch (error) {
    console.error('Error starting new conversation:', error);
  }
}

async function handleStartNew() {
  if (limitStatus.value?.severity === 'error') {
    await confirmStartNew();
  } else {
    showConfirmDialog.value = true;
  }
}

// Insights methods
function toggleInsightsPanel() {
  showInsightsPanel.value = !showInsightsPanel.value;
  if (showInsightsPanel.value) showFeedbackPanel.value = false;
}

async function sendInsightPrompt(prompt) {
  showInsightsPanel.value = false;
  conversationInput.value = prompt;
  await nextTick();
  sendMessage();
}

function handleEnterKey(event) {
  if (isLoadingResponse.value || isContextLoading.value || !conversationInput.value.trim()) {
    event.preventDefault();
    return;
  }
  event.preventDefault();
  sendMessage();
}

// Feedback methods
function toggleFeedbackPanel() {
  showFeedbackPanel.value = !showFeedbackPanel.value;
  if (showFeedbackPanel.value) showInsightsPanel.value = false;
  if (showFeedbackPanel.value && aiStore.assistant.conversationFeedback) {
    feedbackRating.value = aiStore.assistant.conversationFeedback.rating;
    feedbackComment.value = aiStore.assistant.conversationFeedback.comment || '';
  }
}

async function submitConversationFeedback() {
  if (!feedbackRating.value) return;
  feedbackSubmitting.value = true;
  try {
    await aiStore.submitConversationFeedback({
      rating: feedbackRating.value,
      comment: feedbackComment.value || null,
    });
    showFeedbackPanel.value = false;
  } catch (error) {
    console.error('Error submitting conversation feedback:', error);
  } finally {
    feedbackSubmitting.value = false;
  }
}

async function handleMessageFeedback(messageId, rating) {
  if (!messageId) return;
  try {
    await aiStore.submitMessageFeedback({ messageId, rating });
  } catch (error) {
    console.error('Error submitting message feedback:', error);
  }
}

function handleStop() {
  aiStore.cancelCurrentRequest();
  isStopping.value = true;
}

// Save scroll position when user scrolls
function handleScroll() {
  if (chatMessagesContainer.value) {
    const { scrollTop, scrollHeight, clientHeight } = chatMessagesContainer.value;
    aiStore.saveScrollPosition(contextId, scrollTop);
    // Re-enable auto-scroll when the user scrolls back within 50px of the bottom
    userHasScrolledUp.value = scrollTop + clientHeight < scrollHeight - 50;
  }
}

// Watch for new messages - scroll to bottom to follow chains, unless the user has scrolled up
watch(
  () => conversationMessages.value.length,
  (newLength, oldLength) => {
    if (newLength > oldLength && !userHasScrolledUp.value) {
      nextTick(() => scrollToBottom());
    }
  }
);

// Focus the input when the panel is opened so the user can start typing immediately
watch(
  () => aiStore.isSidePanelOpen,
  isOpen => {
    if (isOpen) {
      // Wait for the panel slide-in transition (300ms) to complete before focusing
      setTimeout(() => inputTextarea.value?.focus(), 320);
    }
  }
);

// Setup on mount: attach listener and restore position
onMounted(() => {
  if (chatMessagesContainer.value) {
    chatMessagesContainer.value.addEventListener('scroll', handleScroll);

    // Restore scroll position if we have messages
    if (conversationMessages.value.length > 0) {
      nextTick(() => {
        const savedPosition = aiStore.getScrollPosition(contextId);
        if (savedPosition > 0) {
          chatMessagesContainer.value.scrollTop = savedPosition;
        }
      });
    }
  }
});

// Clean up scroll listener on unmount
onUnmounted(() => {
  if (chatMessagesContainer.value) {
    chatMessagesContainer.value.removeEventListener('scroll', handleScroll);
  }
});
</script>

<style lang="scss" scoped>
.ai-conversation-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.conversation-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.no-page-context-notice {
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  background: rgba(var(--v-theme-primary), 0.08);
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.25);
  padding: 10px 20px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.75);
  line-height: 1.5;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;

  .empty-conversation {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 40px 20px;
  }
}

.question-group {
  // Base question/answer bubble styles provided by _ai-panels.scss
}

// Animations (slideInUp, pulse) provided by _ai-panels.scss

.chat-input-container {
  padding: 16px 20px 20px;
  border-top: 1px solid rgb(var(--v-theme-outline-variant));
  background: rgb(var(--v-theme-surface));
  position: relative;

  .input-wrapper {
    position: relative;
  }

  .warning-container {
    margin-bottom: 8px;
    min-height: 0;
  }

  .limit-warning {
    display: flex;
    align-items: center;
    border-radius: 4px;
    padding: 6px 10px;

    .warning-text {
      font-size: 11px;
      line-height: 1.3;
    }

    &--info {
      background: rgba(var(--v-theme-info), 0.1);
      border-left: 3px solid rgba(var(--v-theme-info), 0.5);

      .v-icon {
        color: rgb(var(--v-theme-info));
      }

      .warning-text {
        color: rgba(var(--v-theme-on-surface), 0.7);
      }
    }

    &--warning {
      background: rgba(var(--v-theme-warning), 0.12);
      border-left: 3px solid rgb(var(--v-theme-warning));

      .v-icon {
        color: rgb(var(--v-theme-warning));
      }

      .warning-text {
        color: rgba(var(--v-theme-on-surface), 0.8);
      }
    }

    &--error {
      background: rgba(var(--v-theme-error), 0.12);
      border-left: 3px solid rgb(var(--v-theme-error));

      .v-icon {
        color: rgb(var(--v-theme-error));
      }

      .warning-text {
        color: rgba(var(--v-theme-on-surface), 0.8);
      }
    }
  }

  .bottom-actions {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 6px;
    margin-top: 8px;

    &__secondary {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      flex: 1;
    }
  }

  .send-button.v-btn {
    height: 30px !important;
    font-size: 11px;
    text-transform: none;
    font-weight: 400;
    padding: 0 8px !important;
    border-radius: 6px !important;
    border: 1px solid rgb(var(--v-theme-primary)) !important;
    color: white !important;
    background: rgb(var(--v-theme-primary)) !important;
    box-shadow: none !important;
    min-width: unset;
    letter-spacing: normal;
    opacity: 1 !important;
    flex-shrink: 0;

    .v-icon {
      color: white !important;
    }

    &:hover,
    &:focus,
    &:focus-visible {
      background: rgb(var(--v-theme-primary)) !important;
      box-shadow: none !important;
    }

    &.v-btn--disabled {
      opacity: 0.4 !important;
    }

    :deep(.v-btn__overlay),
    :deep(.v-btn__underlay) {
      opacity: 0 !important;
    }
  }

  .new-conversation-btn.v-btn {
    height: 30px !important;
    font-size: 11px;
    text-transform: none;
    font-weight: 400;
    padding: 0 8px !important;
    border-radius: 6px !important;
    border: 1px solid rgb(var(--v-theme-primary)) !important;
    color: rgb(var(--v-theme-primary)) !important;
    background: #edf3f5 !important;
    box-shadow: none !important;
    min-width: unset;
    letter-spacing: normal;
    opacity: 1 !important;
    flex-shrink: 0;

    .v-icon {
      color: rgb(var(--v-theme-primary)) !important;
    }

    &:hover,
    &:focus,
    &:focus-visible {
      border-color: rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-primary)) !important;
      background: #edf3f5 !important;
      box-shadow: none !important;
    }

    &.v-btn--disabled {
      opacity: 0.4 !important;
    }

    :deep(.v-btn__overlay),
    :deep(.v-btn__underlay) {
      opacity: 0 !important;
    }
  }

  .insights-btn.v-btn {
    height: 30px !important;
    font-size: 11px;
    text-transform: none;
    font-weight: 400;
    padding: 0 8px !important;
    border-radius: 6px !important;
    border: 1px solid rgb(var(--v-theme-primary)) !important;
    color: rgb(var(--v-theme-primary)) !important;
    background: #edf3f5 !important;
    box-shadow: none !important;
    min-width: unset;
    letter-spacing: normal;
    opacity: 1 !important;
    flex-shrink: 0;

    .v-icon {
      color: rgb(var(--v-theme-primary)) !important;
    }

    &:hover,
    &:focus,
    &:focus-visible {
      border-color: rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-primary)) !important;
      background: #edf3f5 !important;
      box-shadow: none !important;
    }

    &--active {
      background: rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-on-primary)) !important;

      .v-icon {
        color: rgb(var(--v-theme-on-primary)) !important;
      }
    }

    :deep(.v-btn__overlay),
    :deep(.v-btn__underlay) {
      opacity: 0 !important;
    }
  }

  .insights-panel {
    background: #edf3f5;
    border: 1px solid rgb(var(--v-theme-primary));
    border-radius: 8px;
    padding: 10px 12px;
    margin-top: 8px;

    &__header {
      margin-bottom: 8px;
    }

    &__title {
      font-size: 11px;
      font-weight: 500;
      color: rgba(var(--v-theme-on-surface), 0.7);
      letter-spacing: 0.01em;
    }

    &__chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .insight-chip.v-chip {
      font-size: 11px;
      cursor: pointer;
      background: white !important;
      border: 1px solid rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-primary)) !important;

      &:hover {
        background: rgba(var(--v-theme-primary), 0.08) !important;
      }
    }
  }

  .feedback-btn.v-btn {
    height: 30px !important;
    font-size: 11px;
    text-transform: none;
    font-weight: 400;
    padding: 0 8px !important;
    border-radius: 6px !important;
    border: 1px solid rgb(var(--v-theme-primary)) !important;
    color: rgb(var(--v-theme-primary)) !important;
    background: #edf3f5 !important;
    box-shadow: none !important;
    min-width: unset;
    letter-spacing: normal;
    opacity: 1 !important;
    flex-shrink: 0;

    .v-icon {
      color: rgb(var(--v-theme-primary)) !important;
    }

    &:hover,
    &:focus,
    &:focus-visible {
      border-color: rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-primary)) !important;
      background: #edf3f5 !important;
      box-shadow: none !important;
    }

    &--active {
      background: rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-on-primary)) !important;

      .v-icon {
        color: rgb(var(--v-theme-on-primary)) !important;
      }
    }

    :deep(.v-btn__overlay),
    :deep(.v-btn__underlay) {
      opacity: 0 !important;
    }
  }

  .feedback-panel {
    background: #edf3f5;
    border: 1px solid rgb(var(--v-theme-primary));
    border-radius: 8px;
    padding: 10px 12px;
    margin-top: 8px;

    &__header {
      margin-bottom: 8px;
    }

    &__title {
      font-size: 11px;
      font-weight: 500;
      color: rgba(var(--v-theme-on-surface), 0.7);
      letter-spacing: 0.01em;
    }

    &__body {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .feedback-thumbs {
      display: flex;
      flex-shrink: 0;
      gap: 2px;

      .v-btn {
        color: rgba(var(--v-theme-on-surface), 0.5);

        &.thumb-rated {
          color: rgb(var(--v-theme-primary));
        }

        &.thumb-rated--down {
          color: rgb(var(--v-theme-error));
        }
      }
    }

    .feedback-comment {
      flex: 1;
      font-size: 12px;

      :deep(.v-field) {
        font-size: 12px;
        border-radius: 6px;
        background: white;
      }
    }

    .submit-btn.v-btn {
      height: 30px !important;
      font-size: 11px;
      text-transform: none;
      font-weight: 400;
      padding: 0 10px !important;
      border-radius: 6px !important;
      border: 1px solid rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-primary)) !important;
      background: white !important;
      box-shadow: none !important;
      min-width: unset;
      flex-shrink: 0;
      letter-spacing: normal;
      opacity: 1 !important;

      &:hover,
      &:focus {
        background: white !important;
        box-shadow: none !important;
      }

      &.v-btn--disabled {
        opacity: 0.4 !important;
      }

      :deep(.v-btn__overlay),
      :deep(.v-btn__underlay) {
        opacity: 0 !important;
      }
    }
  }

  .mode-toggle {
    height: 28px;
    flex-shrink: 0;
    border-radius: 6px;
    overflow: hidden;
    // Own the border entirely — no Vuetify variant needed on the wrapper
    border: 1px solid rgb(var(--v-theme-primary)) !important;
    box-shadow: none !important;

    :deep(.v-btn) {
      font-size: 11px;
      text-transform: none;
      height: 28px !important;
      min-width: 52px;
      padding: 0 8px;
      background: transparent;
      color: rgb(var(--v-theme-primary));
      box-shadow: none !important;
      border: none !important;

      .v-icon {
        color: rgb(var(--v-theme-primary));
      }
    }

    // Active/selected button — filled primary with contrast text + icon
    :deep(.v-btn--active) {
      background: rgb(var(--v-theme-primary)) !important;
      color: rgb(var(--v-theme-on-primary)) !important;

      .v-icon {
        color: rgb(var(--v-theme-on-primary)) !important;
      }
    }
  }

  .agent-info-icon {
    margin-left: 3px;
    opacity: 0.6;
    cursor: default;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }
  }

  :deep(.v-textarea) {
    .v-field {
      border-radius: 12px;
      font-size: 13px;
    }

    .v-field__input {
      font-size: 13px;
      line-height: 1.2;
    }

    .v-field__outline {
      color: rgba(var(--v-theme-on-surface), 0.38);
      --v-field-border-width: 1px;
    }

    &.v-input--focused .v-field__outline {
      color: rgba(var(--v-theme-on-surface), 0.38);
      --v-field-border-width: 1px;
    }
  }
}

// Slide-up transition for inline feedback panel
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-top: 0 !important;
}

.slide-up-enter-to,
.slide-up-leave-from {
  opacity: 1;
  max-height: 200px;
}

.message-feedback-actions {
  position: absolute;
  bottom: 4px;
  right: 6px;
  display: flex;
  gap: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
  background: rgba(var(--v-theme-surface), 0.9);
  border-radius: 6px;

  &.visible {
    opacity: 1;
  }

  .v-btn {
    color: rgba(var(--v-theme-on-surface), 0.35);

    &.thumb-rated {
      color: rgb(var(--v-theme-primary));
    }

    &.thumb-rated--down {
      color: rgb(var(--v-theme-error));
    }
  }
}
</style>

<style lang="scss">
// Unscoped — targets .answer-content v-html output and the Vuetify teleported menu overlay

.answer-content {
  font-size: 12px;
  line-height: 1.5;
  // marked outputs HTML with \n characters between elements. If an ancestor
  // has white-space: pre or pre-line, each \n renders as an extra blank line.
  // Reset here so block-element whitespace is ignored normally.
  white-space: normal;

  p {
    margin: 0 0 6px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  // marked generates <p> inside <li> for loose lists — collapse that spacing
  li > p {
    margin: 0;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  code {
    background: rgba(var(--v-theme-on-surface), 0.06);
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 11px;
  }

  pre {
    background: rgba(var(--v-theme-on-surface), 0.04);
    border-radius: 4px;
    padding: 8px 10px;
    margin: 4px 0 8px;
    overflow-x: auto;

    code {
      background: none;
      padding: 0;
      font-size: 11px;
    }
  }

  ul,
  ol {
    margin: 2px 0 8px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 2px;
  }

  h1,
  h2,
  h3 {
    font-weight: 600;
    margin: 10px 0 4px;
    line-height: 1.3;
  }

  h1:first-child,
  h2:first-child,
  h3:first-child {
    margin-top: 0;
  }

  h1 {
    font-size: 1.4em;
  }

  h2 {
    font-size: 1.2em;
  }

  h3 {
    font-size: 1.05em;
  }

  a {
    color: rgb(var(--v-theme-primary));
    text-decoration: underline;
  }

  blockquote {
    border-left: 3px solid rgba(var(--v-theme-on-surface), 0.2);
    margin: 4px 0 8px;
    padding: 4px 10px;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  hr {
    border: none;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    margin: 8px 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 4px 0 10px;
    font-size: inherit;
  }

  th {
    background: rgba(var(--v-theme-on-surface), 0.04);
    font-weight: 600;
    text-align: left;
    padding: 5px 8px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  }

  td {
    padding: 4px 8px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  }

  tbody tr:nth-child(even) {
    background: rgba(var(--v-theme-on-surface), 0.02);
  }
}

.agent-info-card {
  .agent-info-text {
    font-size: 12px;
    line-height: 1.4;
    color: rgba(var(--v-theme-on-surface), 0.87);
    margin: 0;
  }

  .agent-info-label {
    font-size: 11px;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.5);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
  }

  .agent-info-prompt {
    display: flex;
    align-items: center;
    font-size: 11px;
    font-style: italic;
    color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.06);
    border: 1px solid rgba(var(--v-theme-primary), 0.2);
    border-radius: 6px;
    padding: 5px 8px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(var(--v-theme-primary), 0.12);
    }
  }

  .agent-info-footer {
    font-size: 11px;
    color: rgba(var(--v-theme-on-surface), 0.45);
    line-height: 1.4;
    margin: 0;
  }
}
</style>
